import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Mail, GraduationCap, Scale } from "lucide-react";
import { firm } from "@/config/firm.config";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const mailTo = (subject, body) =>
  `mailto:${firm.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const ROLES = [
  {
    id: "internship",
    icon: GraduationCap,
    kicker: "For Law Students",
    title: "Internships",
    body: "The chambers offers internships to law students seeking practical exposure to litigation and advisory work. Interns assist with legal research, drafting, case preparation and observing proceedings before the Rajasthan High Court, District and Sessions Courts, and tribunals — under the supervision of enrolled advocates.",
    points: [
      "Research, drafting and documentation",
      "Court observation and case briefing",
      "Guidance from practising advocates",
    ],
    subject: "Internship Application — Nexprime Law Chambers LLP",
    body_prefill:
      "Dear Nexprime Law Chambers LLP,\n\nI wish to apply for an internship at the chambers.\n\nName:\nCollege / University:\nYear of Study:\nPreferred Duration:\nContact Number:\n\nMy resume is attached for your consideration.\n\nThank you.",
    cta: "Apply for Internship",
  },
  {
    id: "associate",
    icon: Scale,
    kicker: "For Advocates",
    title: "Associates",
    body: "The chambers welcomes applications from enrolled advocates looking to build a serious litigation and advisory practice. Associates handle briefs across civil, criminal, cyber, corporate and matrimonial matters, and remain personally responsible for the work entrusted to them.",
    points: [
      "Diverse civil and criminal briefs",
      "Advisory and drafting responsibilities",
      "A practice built on integrity and dignity",
    ],
    subject: "Associate Application — Nexprime Law Chambers LLP",
    body_prefill:
      "Dear Nexprime Law Chambers LLP,\n\nI wish to apply for an associate position at the chambers.\n\nName:\nBar Council Enrolment No.:\nYears of Practice:\nAreas of Practice:\nContact Number:\n\nMy resume is attached for your consideration.\n\nThank you.",
    cta: "Apply as Associate",
  },
];

export default function Careers() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Careers — ${firm.name}`;
  }, []);

  return (
    <div className="min-h-screen bg-navy-900 text-paper" data-testid="careers-page">
      {/* Header */}
      <header className="border-b border-gold/20 bg-navy-900/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="careers-home-logo" aria-label={`${firm.name} — home`} className="focus-ring">
            <Logo />
          </Link>
          <Link
            to="/"
            data-testid="careers-back-link"
            className="focus-ring inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:text-gold-light"
          >
            <ArrowLeft size={14} /> Back to Site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(700px 380px at 20% 10%, rgba(224,184,120,0.14), transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Careers</p>
            <h1 className="mt-6 font-serif text-4xl font-light tracking-tight text-paper sm:text-5xl lg:text-6xl">
              Build your practice with the chambers.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink">
              Nexprime Law Chambers LLP invites law students and enrolled advocates to grow with a chamber founded on
              integrity, diligence and professional dignity. Those interested may apply by email with their resume.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Roles */}
      <section className="relative pb-16 md:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12">
          <div className="grid gap-8 lg:grid-cols-2">
            {ROLES.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal
                  key={r.id}
                  delay={0.1 * i}
                  className="flex flex-col border border-gold/25 bg-navy-800/40 p-8 transition-colors duration-500 hover:border-gold/50 md:p-10"
                  data-testid={`careers-role-${r.id}`}
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center border border-gold/40 text-gold">
                    <Icon size={22} strokeWidth={1.4} aria-hidden="true" />
                  </span>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{r.kicker}</p>
                  <h2 className="mt-3 font-serif text-3xl text-paper">{r.title}</h2>
                  <p className="mt-5 text-base leading-relaxed text-ink">{r.body}</p>
                  <ul className="mt-6 space-y-3">
                    {r.points.map((p) => (
                      <li key={p} className="flex gap-3 text-sm leading-relaxed text-paper/85">
                        <span aria-hidden="true" className="mt-2 inline-block h-px w-3 flex-shrink-0 bg-gold" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={mailTo(r.subject, r.body_prefill)}
                    data-testid={`careers-apply-${r.id}`}
                    className="focus-ring group mt-8 inline-flex items-center justify-between gap-4 border border-gold bg-gold/10 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-light transition-colors duration-300 hover:bg-gold hover:text-navy-900"
                  >
                    <span className="inline-flex items-center gap-3">
                      <Mail size={15} strokeWidth={1.6} aria-hidden="true" /> {r.cta}
                    </span>
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </Reveal>
              );
            })}
          </div>

          {/* How to apply */}
          <Reveal className="mx-auto mt-16 max-w-4xl">
            <div className="border border-gold/40 bg-navy-800/60 p-6 md:p-10" data-testid="careers-howto-block">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">How to Apply</p>
              <p className="mt-4 text-base leading-relaxed text-paper/90">
                Send your resume, along with a brief note about your background and the role you are applying for, to{" "}
                <a
                  href={`mailto:${firm.email}`}
                  data-testid="careers-email-link"
                  className="focus-ring text-gold-light hover:underline"
                >
                  {firm.email}
                </a>
                . Please remember to attach your resume before sending. Suitable applicants will be contacted during
                office hours.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
